import React, { useState, useEffect, useRef } from "react";
import {
  callErrorToast,
  cleanObject,
  getNextWorkingDay,
  removeError,
} from "../../../utilities/utilities";
import { ErrorResponse } from "../../../Models/Error";
import {
  Collection,
  convertCollectionToCreate,
  convertCollectionToUpdate,
  defaultCollection,
  validate,
} from "../../../Models/Collection";
import { CollectionHandlerContext, CollectionHandlerProviderProps } from ".";
import { CollectionsService } from "../../../services/shipment/collections.ts";
import { CarrierConfigurationsService } from "../../../services/contract/carrierConfigurations.ts";
import { Shipment } from "../../../Models/Shipment.ts";
import { CarriersService } from "../../../services/registry/carriers.ts";
import { Carrier } from "../../../Models/Carrier.ts";

/* ===========================================================================
 * SERVICE INSTANTIATION
 * =========================================================================== */
const collectionsService = new CollectionsService();
const carrierConfigurationsService = new CarrierConfigurationsService();
const carriersService = new CarriersService();

/* ===========================================================================
 * PROVIDER: CollectionHandlerProvider
 * =========================================================================== */

/**
 * @component CollectionHandlerProvider
 * Orchestrates the business logic for a single Collection entity.
 * * Responsibilities:
 * - Manages initialization (fetching existing data or setting defaults).
 * - Handles validation and error state.
 * - Manages persistence (create/edit) and advanced workflows (booking).
 * - Coordinates shipment-to-collection associations.
 *
 * @param {CollectionHandlerProviderProps} props - Configuration for the handler context.
 * @returns {JSX.Element} A Context Provider for collection management.
 */
const CollectionHandlerProvider: React.FC<CollectionHandlerProviderProps> = ({
  children,
  id,
  action,
  title,
  carrierCode,
  standAlone = false,
  shipmentId,
  autosave,
  defaultAddressBookId,
  callback,
}) => {
  /* ---------------------------------------------------------------------------
   * STATE & REFS
   * ------------------------------------------------------------------------- */
  const [data, setData] = useState<Partial<Collection>>({});
  const [relatedShipments, setRelatedShipments] = useState<Shipment[]>([]);
  const [loader, setLoader] = useState<boolean>(true);
  const [error, setError] = useState<ErrorResponse<Collection> | null>(null);
  const prevError = useRef<ErrorResponse<Collection> | null>(null);

  /* ---------------------------------------------------------------------------
   * DATA FETCHING
   * ------------------------------------------------------------------------- */

  /**
   * Fetches a single collection by ID.
   * * @async
   * @param {string} id - The unique identifier of the collection.
   * @returns {Promise<Collection | undefined>}
   */
  const getCollection = async (id: string): Promise<Collection | undefined> => {
    setLoader(true);
    try {
      const res = await collectionsService.get(id);
      return res;
    } catch (err) {
      callErrorToast(err);
      return undefined;
    } finally {
      setLoader(false);
    }
  };

  /**
   * Fetches all shipments related to the current collection.
   * * @async
   * @param {string} id - The collection ID.
   * @returns {Promise<Shipment[] | []>}
   */
  const getRelatedShipments = async (id: string): Promise<Shipment[] | []> => {
    setLoader(true);
    try {
      const res = await collectionsService.getRelatedShipments(id);
      return res;
    } catch (err) {
      callErrorToast(err);
      return [];
    } finally {
      setLoader(false);
    }
  };

  /**
   * Retrieves carrier details based on carrier code.
   * * @async
   * @param {string} code - The carrier code.
   * @returns {Promise<Carrier | undefined>}
   */
  const getCarrier = async (code: string): Promise<Carrier | undefined> => {
    try {
      const res = await carriersService.get(code);
      return res;
    } catch (err) {
      callErrorToast(err);
      return undefined;
    }
  };

  /* ---------------------------------------------------------------------------
   * PERSISTENCE LOGIC
   * ------------------------------------------------------------------------- */

  /**
   * Validates and saves the collection. Automatically handles create vs edit.
   * * @async
   * @param {Collection} collection - The collection data to save.
   * @param {keyof Collection | null} property - Field whose error should be cleared.
   * @param {boolean} [save=true] - If false, only updates local state without API call.
   * @param {boolean} [isSubCall=false] - If true, prevents internal state updates and loaders.
   * @returns {Promise<Collection | void>}
   */
  const saveCollection = async (
    collection: Collection,
    property: keyof Collection | null,
    save = true,
    isSubCall = false,
  ): Promise<Collection | void> => {
    // 1. Clean object and manage internal state/errors
    const cleaned = cleanObject(collection) as Collection;
    if (property) removeError(property, error, prevError, setError);
    if (!isSubCall) setData(cleaned);

    // 2. Short-circuit if persistence is not required
    if (!save) return;
    if (!autosave && !isSubCall) setLoader(true);

    // 3. Validation
    const validation = validate(cleaned, standAlone);
    if (!validation.isValid) {
      if (!autosave || cleaned.id) {
        setError({ response: { data: validation.errors } });
      }
      if (!isSubCall) setLoader(false);
      return;
    }

    // 4. Persistence (Create or Edit)
    try {
      let savedCollection: Collection;
      if (cleaned.id) {
        savedCollection = await edit(cleaned);
      } else {
        savedCollection = await create(cleaned);
      }

      if (isSubCall) return savedCollection;

      setData(savedCollection);
      callback?.(savedCollection);
    } finally {
      if (!isSubCall) setLoader(false);
    }
  };

  /**
   * Advanced workflow: Saves the collection, links a shipment, and finalizes by booking.
   * * @async
   * @param {Collection} collection - Data to save.
   * @param {keyof Collection | null} property - Field to clear from errors.
   * @param {boolean} [save=true] - Whether to perform persistence.
   * @returns {Promise<void>}
   */
  const saveCollectionAndBook = async (
    collection: Collection,
    property: keyof Collection | null,
    save = true,
  ): Promise<void> => {
    setLoader(true);
    try {
      // 1. Save Collection as sub-call to handle state manually
      const savedCollection = (await saveCollection(
        collection,
        property,
        save,
        true,
      )) as Collection | undefined;

      if (savedCollection) {
        // 2. Link shipment if required
        if (shipmentId && !standAlone) {
          await createShipmentRelation(savedCollection.id, shipmentId);
        }

        // 3. Finalize by booking
        await book(savedCollection.id);

        setData(savedCollection);
        callback?.(savedCollection);
      }
    } finally {
      setLoader(false);
    }
  };

  /**
   * Internal helper to perform create API request.
   * @async
   * @param {Collection} collection
   * @returns {Promise<Collection>}
   */
  const create = async (collection: Collection): Promise<Collection> => {
    try {
      const payload = convertCollectionToCreate(collection);
      return await collectionsService.create(payload);
    } catch (err) {
      setError(err as ErrorResponse<Collection>);
      throw err;
    }
  };

  /**
   * Internal helper to perform update API request.
   * @async
   * @param {Collection} collection
   * @returns {Promise<Collection>}
   */
  const edit = async (collection: Collection): Promise<Collection> => {
    try {
      const payload = convertCollectionToUpdate(collection);
      await collectionsService.edit(payload);
      return collection;
    } catch (err) {
      setError(err as ErrorResponse<Collection>);
      throw err;
    }
  };

  /**
   * Links a shipment to a specific collection.
   * @async
   * @param {string} id - Collection ID.
   * @param {string} shipmentId - Shipment ID.
   * @returns {Promise<void>}
   */
  const createShipmentRelation = async (
    id: string,
    shipmentId: string,
  ): Promise<void> => {
    try {
      await collectionsService.createShipmentRelation(id, shipmentId);
    } catch (err) {
      callErrorToast(err);
    }
  };

  /**
   * Finalizes a collection by booking it via API.
   * @async
   * @param {string} id - Collection ID.
   * @returns {Promise<void>}
   */
  const book = async (id: string): Promise<void> => {
    try {
      await collectionsService.book(id);
    } catch (err) {
      callErrorToast(err);
    }
  };

  /* ---------------------------------------------------------------------------
   * EFFECTS
   * ------------------------------------------------------------------------- */

  /**
   * Initialization effect: loads configuration, existing data, and related shipments.
   */
  useEffect(() => {
    const initialize = async () => {
      setLoader(true);
      let newData: Partial<Collection> = {
        ...defaultCollection,
        date: getNextWorkingDay(),
      };

      try {
        // 1. Resolve Carrier if code is present
        if (carrierCode) {
          const carrier = await getCarrier(carrierCode);
          newData.carrier = carrier;
        }

        // 2. Load existing data if ID is present
        if (id) {
          const existingCollection = await getCollection(id);
          if (existingCollection) {
            newData = { ...newData, ...existingCollection };
          }

          // 3. Load related shipments for details view
          if (action === "details") {
            const related = await getRelatedShipments(id);
            setRelatedShipments(related);
          }
        }
        setData(newData);
      } finally {
        setLoader(false);
      }
    };

    initialize();
  }, [id, carrierCode]);

  /* ---------------------------------------------------------------------------
   * RENDER
   * ------------------------------------------------------------------------- */

  return (
    <CollectionHandlerContext.Provider
      value={{
        id,
        action,
        data,
        loader,
        error,
        title,
        autosave,
        standAlone,
        defaultAddressBookId,
        relatedShipments,
        callback,
        saveCollection,
        saveCollectionAndBook,
      }}
    >
      {children}
    </CollectionHandlerContext.Provider>
  );
};

export { CollectionHandlerProvider };
